import type { GhmSignalKind } from "@ghm/mqtt-contract";
import type { LatestPort } from "../devices/types";
import type { Address, ChannelAddress, Machine, SignalType } from "./types";

const REAL_ADDRESS_RE = /^S(\d+)\.P(\d+)$/i;
const DERIVED_ADDRESS_RE = /^V(\d+)$/i;
const TAG_RE = /^G(\d+)_P(\d+)_(v|acc|s)$/i;

/** Sinal default quando a notacao tecnica omite (ex.: `S0.P1` sem sufixo). */
export const DEFAULT_SIGNAL: SignalType = "acc";

export function formatAddress(address: Address): string {
  if (address.kind === "real") {
    return `S${address.slotIndex}.P${address.portIndex}`;
  }

  return `V${address.derivedIndex}`;
}

export function parseAddress(value: string): Address | null {
  const trimmed = value.trim();
  const real = REAL_ADDRESS_RE.exec(trimmed);
  if (real) {
    return {
      kind: "real",
      slotIndex: Number(real[1]),
      portIndex: Number(real[2]),
    };
  }

  const derived = DERIVED_ADDRESS_RE.exec(trimmed);
  if (derived) {
    return {
      kind: "derived",
      derivedIndex: Number(derived[1]),
    };
  }

  return null;
}

export function resolveSnapshot(
  address: ChannelAddress,
  machine: Machine,
  latestPorts: LatestPort[],
): LatestPort | undefined {
  const slot = machine.slots.find((candidate) => candidate.slotIndex === address.slotIndex);
  if (!slot) return undefined;

  return latestPorts.find(
    (port) => port.deviceId === slot.deviceId && port.portIndex === address.portIndex,
  );
}

export function nextDerivedIndex(usedIndexes: Iterable<number>): number {
  const used = new Set(usedIndexes);
  let index = 0;
  while (used.has(index)) index += 1;
  return index;
}

/**
 * Formata um endereco de canal como tag publica: `G{slot}_P{port}_{signal}`.
 * Quando `signal` esta ausente no endereco, usa `DEFAULT_SIGNAL` (`acc`).
 */
export function formatTag(address: ChannelAddress): string {
  const signal = address.signal ?? DEFAULT_SIGNAL;
  return `G${address.slotIndex}_P${address.portIndex}_${signal}`;
}

export function formatPublicChannelLabel(address: ChannelAddress): string {
  const inputLabel = `Entrada ${address.portIndex + 1}`;
  if (address.slotIndex === 0) {
    return inputLabel;
  }

  return `Gateway ${address.slotIndex + 1} - ${inputLabel}`;
}

/**
 * Rotulo generico do sinal. O `kind` e opcional e MUDA a resposta so para `v`:
 *
 * Ate 08/2026 esta funcao devolvia "Velocidade" para TODO `v`, e como ela
 * alimenta a legenda dos cards, uma tensao Modbus aparecia como
 * "Entrada 7 - Velocidade". O palpite acertava no encoder e errava em todo o
 * resto — que e exatamente o defeito que o `kind` existe para fechar.
 *
 * `rate` descreve uma taxa, que pode ser velocidade, vazao ou potencia. O
 * fallback nao pode escolher a grandeza; o nome declarado identifica o sinal.
 * Sem `kind` conhecido, o comportamento legado permanece.
 */
/**
 * Palavra que descreve o TIPO do sinal no rotulo tecnico ("Entrada 5 - Leitura").
 *
 * `kind` e opcional porque widget antigo pode nao ter a semantica gravada — mas
 * quem TEM precisa passar. Este ramo nasceu junto com a Etapa 1 e ficou morto:
 * os dez componentes que montam legenda chamavam sem `kind`, entao todo `_v`
 * caia no default historico e um nivel de tanque aparecia como "Velocidade".
 * Ao ler dado real do simulador foi o que mais saltou aos olhos.
 */
export function formatPublicSignalType(
  signal: SignalType = DEFAULT_SIGNAL,
  kind?: GhmSignalKind,
): string {
  switch (signal) {
    case "s":
      return "Estado";
    case "v":
      if (kind === "cumulative") return "Total";
      if (kind === "state") return "Codigo";
      // Os kinds nao identificam a grandeza fisica nem o estado da maquina.
      if (kind === "instantaneous") return "Leitura";
      if (kind === "rate") return "Taxa";
      return "Velocidade";
    case "acc":
    default:
      return "Total";
  }
}

export function formatPublicSignalLabel(
  address: ChannelAddress,
  kind?: GhmSignalKind,
): string {
  return `${formatPublicChannelLabel(address)} - ${formatPublicSignalType(address.signal, kind)}`;
}

export function formatPublicSourceLabel(source: string, kind?: GhmSignalKind): string {
  const tag = parseTag(source);
  if (tag) return formatPublicSignalLabel(tag, kind);

  const address = parseAddress(source);
  if (!address) return source;
  if (address.kind === "derived") return `Calculo V${address.derivedIndex}`;

  return formatPublicSignalLabel({ ...address, signal: DEFAULT_SIGNAL }, kind);
}

/**
 * Parse de tag publica (`G0_P1_v` / `G0_P1_acc` / `G0_P1_s`). Tambem aceita a
 * notacao tecnica antiga `S0.P1` (com `signal` defaultado para `"acc"`) para
 * facilitar a migracao de calculos salvos no `localStorage`.
 */
export function parseTag(value: string): ChannelAddress | null {
  const trimmed = value.trim();

  const tag = TAG_RE.exec(trimmed);
  if (tag) {
    return {
      kind: "real",
      slotIndex: Number(tag[1]),
      portIndex: Number(tag[2]),
      signal: tag[3].toLowerCase() as SignalType,
    };
  }

  const legacy = parseAddress(trimmed);
  if (legacy && legacy.kind === "real") {
    return { ...legacy, signal: legacy.signal ?? DEFAULT_SIGNAL };
  }
  return null;
}
